import { create } from 'zustand';
import { getPriceIndexes } from '../api/generated/price-indexes/price-indexes';
import { customInstance } from '../api/mutator/custom-instance';
import { useToastStore } from './useToastStore';
import type {
  FacilityFlowCoreModulesServicesDtosPriceIndexDto,
  FacilityFlowCoreModulesServicesDtosCreatePriceIndexRequest,
  FacilityFlowCoreModulesServicesDtosApplyPriceIndexResponse,
} from '../api/generated/models';

type PriceIndexDto = FacilityFlowCoreModulesServicesDtosPriceIndexDto;
type CreatePriceIndexRequest = FacilityFlowCoreModulesServicesDtosCreatePriceIndexRequest;
type ApplyPriceIndexResponse = FacilityFlowCoreModulesServicesDtosApplyPriceIndexResponse;

const {
  postApiV1PriceIndexes,
  getApiV1PriceIndexesId,
  postApiV1PriceIndexesIdApply,
  getApiV1PriceIndexesIdPreview,
} = getPriceIndexes();

interface PriceIndexState {
  priceIndexes: PriceIndexDto[];
  activePriceIndex: PriceIndexDto | null;
  selectedPriceIndex: PriceIndexDto | null;
  previewData: ApplyPriceIndexResponse | null;
  loading: boolean;
  saving: boolean;
  error: string | null;

  fetchPriceIndexes: () => Promise<void>;
  fetchActivePriceIndex: () => Promise<void>;
  getPriceIndexById: (id: string) => Promise<PriceIndexDto | null>;
  createPriceIndex: (data: CreatePriceIndexRequest) => Promise<PriceIndexDto | null>;
  applyPriceIndex: (id: string) => Promise<void>;
  previewPriceIndex: (id: string) => Promise<ApplyPriceIndexResponse | null>;
}

export const usePriceIndexStore = create<PriceIndexState>((set) => ({
  priceIndexes: [],
  activePriceIndex: null,
  selectedPriceIndex: null,
  previewData: null,
  loading: false,
  saving: false,
  error: null,

  fetchPriceIndexes: async () => {
    set({ loading: true, error: null });
    try {
      const response = await customInstance<PriceIndexDto[]>({
        url: '/api/v1/price-indexes',
        method: 'GET',
      });
      set({ priceIndexes: response || [], loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Fehler beim Laden der Preisindizes',
        loading: false,
      });
    }
  },

  fetchActivePriceIndex: async () => {
    set({ loading: true, error: null });
    try {
      const activeIndex = await customInstance<PriceIndexDto>({
        url: '/api/v1/price-indexes/active',
        method: 'GET',
      });
      set({ activePriceIndex: activeIndex || null, loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Fehler beim Laden des aktiven Preisindex',
        loading: false,
      });
    }
  },

  getPriceIndexById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const priceIndex = await getApiV1PriceIndexesId(id);
      set({ selectedPriceIndex: priceIndex, loading: false });
      return priceIndex;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Fehler beim Laden des Preisindex',
        loading: false,
        selectedPriceIndex: null,
      });
      return null;
    }
  },

  createPriceIndex: async (data: CreatePriceIndexRequest) => {
    set({ saving: true, error: null });
    try {
      const response = await postApiV1PriceIndexes(data);
      set({ saving: false });

      useToastStore.getState().addToast('Preisindex erfolgreich erstellt', 'success');
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Erstellen des Preisindex';
      set({
        error: errorMessage,
        saving: false,
      });

      useToastStore.getState().addToast(errorMessage, 'error');
      throw error;
    }
  },

  applyPriceIndex: async (id: string) => {
    set({ saving: true, error: null });
    try {
      await postApiV1PriceIndexesIdApply(id, { previewOnly: false });
      set({ saving: false });

      useToastStore.getState().addToast('Preisindex erfolgreich angewendet', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Anwenden des Preisindex';
      set({
        error: errorMessage,
        saving: false,
      });

      useToastStore.getState().addToast(errorMessage, 'error');
      throw error;
    }
  },

  previewPriceIndex: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const preview = await getApiV1PriceIndexesIdPreview(id);
      set({ previewData: preview, loading: false });
      return preview;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Fehler beim Laden der Vorschau',
        loading: false,
        previewData: null,
      });
      return null;
    }
  },
}));
